/**
 * 水印组件
 *
 * 功能说明：
 * - 为 ECharts 图表添加水印功能
 * - 支持明亮和暗色主题水印图片
 * - 自动处理水印在图表中的位置和层级
 *
 * 使用示例：
 * ```javascript
 * const option = {
 *   // 其他图表配置...
 *   dvWaterMark: {
 *     show: true,
 *     style: {
 *       image: 'light', // 或 'dark'，也可以是自定义图片 URL
 *       width: 36,
 *       height: 10,
 *       // 其他样式配置...
 *     }
 *   }
 * };
 *
 * // 如果不提供 dvWaterMark 配置，将使用默认的明亮主题水印
 * ```
 */

import { type EChartsExtensionInstallRegisters } from 'echarts/types/src/extension';
import { imgs } from '../../assets/dvWaterMarkImgs.js';

const waterMarkZ = 57;
const waterMarkId = '$DV_WATER_MARK';

/**
 * 配置水印样式
 * @param dvWaterMarkModel - 水印模型对象
 */
function configureWaterMarkStyle(dvWaterMarkModel) {
  const waterMarkStyle = dvWaterMarkModel.style;
  if (waterMarkStyle) {
    // 根据主题设置对应的水印图片
    if (dvWaterMarkModel.style.image === 'light') {
      dvWaterMarkModel.style.image = imgs.light;
    }
    if (dvWaterMarkModel.style.image === 'dark') {
      dvWaterMarkModel.style.image = imgs.dark;
    }
  } else {
    // 如果没有样式配置，设置默认样式
    dvWaterMarkModel.style = { height: 10, width: 36 };
    dvWaterMarkModel.style.image = imgs.light;
  }
}

/**
 * 设置水印默认属性
 * @param dvWaterMarkModel - 水印模型对象
 */
function setWaterMarkDefaults(dvWaterMarkModel) {
  if (!dvWaterMarkModel.id) {
    dvWaterMarkModel.id = waterMarkId;
  }
  if (!dvWaterMarkModel.type) {
    dvWaterMarkModel.type = 'image';
  }
  if (!dvWaterMarkModel.z) {
    dvWaterMarkModel.z = waterMarkZ;
  }
  if (!dvWaterMarkModel.silent) {
    dvWaterMarkModel.silent = true;
  }
}

/**
 * 将水印添加到图表的 graphic 配置中
 * @param option - ECharts 配置对象
 * @param dvWaterMarkModel - 水印模型对象
 */
function addWaterMarkToGraphic(option, dvWaterMarkModel) {
  // 如果没有 graphic 配置，添加一个
  if (!option.graphic) {
    option.graphic = { elements: [dvWaterMarkModel] };
  } else if (option.graphic.elements) {
    // 如果 graphic 里面有 elements
    option.graphic.elements.push(dvWaterMarkModel);
  } else if (Array.isArray(option.graphic)) {
    // 如果 graphic 是一个数组
    option.graphic[0].elements.push(dvWaterMarkModel);
  } else {
    const elements = option.graphic;
    option.graphic.elements = [elements, dvWaterMarkModel];
  }
}

/**
 * 水印处理主函数
 * @param option - ECharts 配置对象
 */
function HiThinkWaterMark(option) {
  // 获取配置项中的水印设置
  const dvWaterMarkModel = option.dvWaterMark;
  if (!dvWaterMarkModel || dvWaterMarkModel.show === false) {
    return;
  }

  configureWaterMarkStyle(dvWaterMarkModel);
  setWaterMarkDefaults(dvWaterMarkModel);
  addWaterMarkToGraphic(option, dvWaterMarkModel);
}
export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerPreprocessor(function (...args) {
    const option = args[0];
    HiThinkWaterMark(option);
  });
}
